import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { useAdminGetDeliveryCities } from '../../../delivery-cities/hooks/admin-get-delivery-cities.hook';
import { ADMIN_DELIVERY_CITIES_APP_PATHS } from '../../../delivery-cities/constants/delivery-cities-app-paths.constants';
import { DeliveryZoneCard } from './DeliveryZoneCard.component';

type Props = {
  zoneId: string;
};

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.admin.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  tableContainer: {
    maxHeight: '55svh',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0 1rem',
  },
  hint: {
    margin: 0,
  },
}));

export function DeliveryZoneCitiesCard({ zoneId }: Props) {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { isLoading, totalCount, deliveryCities } = useAdminGetDeliveryCities({
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'name',
    sortDirection: 'asc',
    filters: { zoneId },
  });

  const hasCities = !isLoading && deliveryCities.length > 0;

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <DeliveryZoneCard title={isLoading ? 'Villes de la zone' : `Villes de la zone (${totalCount})`}>
      {isLoading && (
        <Table size="small">
          <TableBodySkeleton rows={3} columns={3} />
        </Table>
      )}
      {!isLoading && deliveryCities.length === 0 && <p>Aucune ville dans cette zone</p>}
      {hasCities && (
        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Ville</TableCell>
                <TableCell>Code postal</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryCities.map((city) => (
                <TableRow key={city.id} hover>
                  <TableCell>
                    <Link
                      className={classes.link}
                      to={ADMIN_DELIVERY_CITIES_APP_PATHS.DELIVERY_CITY_DETAILS_PATH(city.id)}
                    >
                      {city.name}
                    </Link>
                  </TableCell>
                  <TableCell>{city.zipCode}</TableCell>
                  <TableCell>
                    <StatusChip isActive={city.isActive} archived={city.archivedAt !== null} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <div className={classes.footer}>
        <FormHelperText className={classes.hint}>Pour changer la zone d'une ville, modifiez la ville.</FormHelperText>
        {hasCities && (
          <TablePagination
            component="div"
            size="small"
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => handleRowsPerPageChange(parseInt(event.target.value, 10))}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          />
        )}
      </div>
    </DeliveryZoneCard>
  );
}
